import { supabase } from '../config/supabase';
import { TemplateType } from '../components/import/MonthlyDataImporter';
import { MonthlyStats } from '../types/property';
import { createMonthlyStats } from './stats/monthlyStats';
import { updatePropertyStats } from './stats/propertyStats';

// Interfaz para manejar la importación de datos
interface ImportResult {
  created: number;
  updated: number;
  skipped: number;
  errors: string[];
}

// Función para limpiar y convertir valores monetarios (ej: "628 €" -> 628)
const cleanMonetaryValue = (value: string | number): number => {
  if (typeof value === 'number') return value;
  
  // Eliminar el símbolo de euro y cualquier espacio
  const cleanValue = value.replace(/[€\s]/g, '');
  // Convertir comas a puntos si es necesario
  const normalizedValue = cleanValue.replace(',', '.');
  // Convertir a número
  const numValue = parseFloat(normalizedValue);
  
  return isNaN(numValue) ? 0 : numValue;
};

// Función para limpiar y convertir porcentajes (ej: "64%" -> 64)
const cleanPercentageValue = (value: string | number): number => {
  if (typeof value === 'number') return value;
  
  // Eliminar el símbolo de porcentaje y cualquier espacio
  const cleanValue = value.replace(/[%\s]/g, '');
  // Convertir comas a puntos si es necesario
  const normalizedValue = cleanValue.replace(',', '.');
  // Convertir a número
  const numValue = parseFloat(normalizedValue);
  
  return isNaN(numValue) ? 0 : numValue;
};

// Función para importar datos mensuales desde una plantilla de datos mensuales por propiedad
const importMonthlyByProperty = async (data: any[], propertyId: string): Promise<void> => {
  if (!data || data.length === 0) {
    throw new Error('No hay datos para importar');
  }

  if (!propertyId) {
    throw new Error('Se requiere un ID de propiedad para importar datos mensuales');
  }

  // Verificar que la propiedad existe
  const { data: propertyData, error: propertyError } = await supabase
    .from('properties')
    .select('id, name')
    .eq('id', propertyId)
    .single();

  if (propertyError || !propertyData) {
    throw new Error(`La propiedad con ID ${propertyId} no existe`);
  }

  // Transformar los datos para insertarlos en la base de datos
  const monthlyData = data.map((item, index) => {
    // Validar campos obligatorios
    if (!item['Mes'] || !item['Año']) {
      throw new Error(`Fila ${index + 1}: Mes y Año son obligatorios`);
    }

    // Convertir y validar campos
    const month = Number(item['Mes']);
    const year = Number(item['Año']);
    
    if (isNaN(month) || month < 1 || month > 12) {
      throw new Error(`Fila ${index + 1}: Mes debe ser un número entre 1 y 12`);
    }
    
    if (isNaN(year) || year < 2000 || year > 2100) {
      throw new Error(`Fila ${index + 1}: Año debe ser un número válido`);
    }

    // Convertir valores monetarios y porcentajes
    const revenue = cleanMonetaryValue(item['Facturación'] || 0);
    const cleaningExpenses = cleanMonetaryValue(item['Gastos Limpieza'] || 0);
    const inmohostExpenses = cleanMonetaryValue(item['Gastos InmoHost'] || 0);
    const profit = cleanMonetaryValue(item['Beneficio'] || 0);
    const occupancyRate = cleanPercentageValue(item['Ocupación (%)'] || 0);
    const reservations = Number(item['Reservas'] || 0);
    const pricePerNight = cleanMonetaryValue(item['Precio Promedio'] || 0);
    const nightsStayed = Number(item['Duración Estancia'] || 0);

    // Devolver objeto con los datos validados
    return {
      property_id: propertyId,
      property_name: propertyData.name,
      month,
      year,
      revenue,
      cleaning_costs: cleaningExpenses,
      inmohost_costs: inmohostExpenses,
      profit,
      occupancy_rate: occupancyRate,
      reservations,
      average_night_price: pricePerNight,
      average_stay_duration: nightsStayed,
      created_at: new Date().toISOString(),
    };
  });

  // Insertar datos en la base de datos
  const { error } = await supabase
    .from('monthly_stats')
    .insert(monthlyData);

  if (error) {
    console.error('Error al importar datos mensuales:', error);
    throw new Error(`Error al importar datos mensuales: ${error.message}`);
  }
};

// Función para importar datos de múltiples propiedades para un mes específico
const importMultipleProperties = async (data: any[], month: number, year: number): Promise<void> => {
  if (!data || data.length === 0) {
    throw new Error('No hay datos para importar');
  }

  if (!month || !year) {
    throw new Error('Se requiere mes y año para importar datos de múltiples propiedades');
  }

  // Transformar los datos para insertarlos en la base de datos
  const monthlyData = await Promise.all(data.map(async (item, index) => {
    // Validar campos obligatorios
    if (!item['ID Propiedad'] || !item['Nombre Propiedad']) {
      throw new Error(`Fila ${index + 1}: ID Propiedad y Nombre Propiedad son obligatorios`);
    }

    const propertyId = item['ID Propiedad'];

    // Verificar que la propiedad existe
    const { data: propertyData, error: propertyError } = await supabase
      .from('properties')
      .select('id')
      .eq('id', propertyId);

    if (propertyError || !propertyData || propertyData.length === 0) {
      throw new Error(`Fila ${index + 1}: La propiedad con ID ${propertyId} no existe`);
    }

    // Convertir valores monetarios y porcentajes
    const revenue = cleanMonetaryValue(item['Facturación'] || 0);
    const cleaningExpenses = cleanMonetaryValue(item['Gastos Limpieza'] || 0);
    const inmohostExpenses = cleanMonetaryValue(item['Gastos InmoHost'] || 0);
    const profit = cleanMonetaryValue(item['Beneficio'] || 0);
    const occupancyRate = cleanPercentageValue(item['Ocupación (%)'] || 0);
    const reservations = Number(item['Reservas'] || 0);
    const pricePerNight = cleanMonetaryValue(item['Precio Promedio'] || 0);
    const nightsStayed = Number(item['Duración Estancia'] || 0);

    // Devolver objeto con los datos validados
    return {
      property_id: propertyId,
      property_name: item['Nombre Propiedad'],
      month,
      year,
      revenue,
      cleaning_costs: cleaningExpenses,
      inmohost_costs: inmohostExpenses,
      profit,
      occupancy_rate: occupancyRate,
      reservations,
      average_night_price: pricePerNight,
      average_stay_duration: nightsStayed,
      created_at: new Date().toISOString(),
    };
  }));

  // Insertar datos en la base de datos
  const { error } = await supabase
    .from('monthly_stats')
    .insert(monthlyData);

  if (error) {
    console.error('Error al importar datos mensuales:', error);
    throw new Error(`Error al importar datos mensuales: ${error.message}`);
  }
};

// Función para importar histórico completo
const importFullHistory = async (data: any[]): Promise<void> => {
  if (!data || data.length === 0) {
    throw new Error('No hay datos para importar');
  }

  // Transformar los datos para insertarlos en la base de datos
  const monthlyData = await Promise.all(data.map(async (item, index) => {
    // Validar campos obligatorios
    if (!item['ID Propiedad'] || !item['Nombre Propiedad'] || !item['Mes'] || !item['Año']) {
      throw new Error(`Fila ${index + 1}: ID Propiedad, Nombre Propiedad, Mes y Año son obligatorios`);
    }

    const propertyId = item['ID Propiedad'];
    const month = Number(item['Mes']);
    const year = Number(item['Año']);

    // Verificar que la propiedad existe
    const { data: propertyData, error: propertyError } = await supabase
      .from('properties')
      .select('id')
      .eq('id', propertyId);

    if (propertyError || !propertyData || propertyData.length === 0) {
      throw new Error(`Fila ${index + 1}: La propiedad con ID ${propertyId} no existe`);
    }

    // Validar mes y año
    if (isNaN(month) || month < 1 || month > 12) {
      throw new Error(`Fila ${index + 1}: Mes debe ser un número entre 1 y 12`);
    }
    
    if (isNaN(year) || year < 2000 || year > 2100) {
      throw new Error(`Fila ${index + 1}: Año debe ser un número válido`);
    }

    // Convertir valores monetarios y porcentajes
    const revenue = cleanMonetaryValue(item['Facturación'] || 0);
    const cleaningExpenses = cleanMonetaryValue(item['Gastos Limpieza'] || 0);
    const inmohostExpenses = cleanMonetaryValue(item['Gastos InmoHost'] || 0);
    const profit = cleanMonetaryValue(item['Beneficio'] || 0);
    const occupancyRate = cleanPercentageValue(item['Ocupación (%)'] || 0);
    const reservations = Number(item['Reservas'] || 0);
    const pricePerNight = cleanMonetaryValue(item['Precio Promedio'] || 0);
    const nightsStayed = Number(item['Duración Estancia'] || 0);

    // Devolver objeto con los datos validados
    return {
      property_id: propertyId,
      property_name: item['Nombre Propiedad'],
      month,
      year,
      revenue,
      cleaning_costs: cleaningExpenses,
      inmohost_costs: inmohostExpenses,
      profit,
      occupancy_rate: occupancyRate,
      reservations,
      average_night_price: pricePerNight,
      average_stay_duration: nightsStayed,
      created_at: new Date().toISOString(),
    };
  }));

  // Insertar datos en la base de datos
  const { error } = await supabase
    .from('monthly_stats')
    .insert(monthlyData);

  if (error) {
    console.error('Error al importar datos mensuales:', error);
    throw new Error(`Error al importar datos mensuales: ${error.message}`);
  }
};

// Función principal para importar datos mensuales
export const importMonthlyData = async (data: any[], templateType: TemplateType, options?: { propertyId?: string, month?: number, year?: number }): Promise<void> => {
  switch (templateType) {
    case TemplateType.MONTHLY_BY_PROPERTY:
      if (!options?.propertyId) {
        throw new Error('Se requiere un ID de propiedad para importar datos mensuales por propiedad');
      }
      return importMonthlyByProperty(data, options.propertyId);
    
    case TemplateType.MULTIPLE_PROPERTIES:
      if (!options?.month || !options?.year) {
        throw new Error('Se requiere mes y año para importar datos de múltiples propiedades');
      }
      return importMultipleProperties(data, options.month, options.year);
    
    case TemplateType.FULL_HISTORY:
      return importFullHistory(data);
    
    default:
      throw new Error('Tipo de plantilla no válido');
  }
};

// Función para obtener datos mensuales de una propiedad
export const getMonthlyDataByProperty = async (propertyId: string): Promise<MonthlyStats[]> => {
  const { data, error } = await supabase
    .from('monthly_stats')
    .select('*')
    .eq('property_id', propertyId)
    .order('year', { ascending: true })
    .order('month', { ascending: true });

  if (error) {
    console.error('Error al obtener datos mensuales:', error);
    throw new Error(`Error al obtener datos mensuales: ${error.message}`);
  }

  return data || [];
};

// Función para obtener datos mensuales de todas las propiedades para un mes específico
export const getMonthlyDataByMonth = async (month: number, year: number): Promise<MonthlyStats[]> => {
  const { data, error } = await supabase
    .from('monthly_stats')
    .select('*')
    .eq('month', month)
    .eq('year', year);

  if (error) {
    console.error('Error al obtener datos mensuales:', error);
    throw new Error(`Error al obtener datos mensuales: ${error.message}`);
  }

  return data || [];
};

// Función para exportar datos mensuales a Excel
export const exportMonthlyData = async (propertyId?: string): Promise<MonthlyStats[]> => {
  let query = supabase
    .from('monthly_stats')
    .select('*');
  
  if (propertyId) {
    query = query.eq('property_id', propertyId);
  }
  
  const { data, error } = await query
    .order('year', { ascending: true })
    .order('month', { ascending: true });

  if (error) {
    console.error('Error al exportar datos mensuales:', error);
    throw new Error(`Error al exportar datos mensuales: ${error.message}`);
  }

  return data || [];
};
